/* I costi di esecuzione misurati, uno per venue.
 *
 * Riportati dal frontend precedente (`sem-bot-ui/src/components/costi/
 * CostiVenue.js`), dove venivano da quote reali del 26/08/2026 su size 10.000
 * USDT più la verifica on-chain sulla transazione del bot live. Non sono
 * stime: sono misure, e per questo si applicano con un tocco invece di essere
 * riscritte a mano ogni volta.
 *
 * Perché contano. Il costo per operazione sembra trascurabile — due punti base
 * — e su una strategia che gira poco lo è. Su una che chiude centoventi giri
 * diventa il cinque per cento del risultato con Velora e il trentuno per cento
 * con 0x, e la differenza fra i due decide se la strategia è praticabile.
 * Scritto a mano quel numero si sbaglia, o si lascia il campo vuoto e si
 * misura una strategia che non esiste.
 *
 * Su un DEX la commissione della pool è GIÀ dentro il prezzo quotato: metterla
 * anche come `fee` la conterebbe due volte. Per questo i venue on-chain hanno
 * commissioni a zero e tutto il costo sta nello slippage, mentre quelli con
 * book (Binance) hanno la commissione vera.
 */

export interface Venue {
  id: string;
  nome: string;
  /** Costo per lato in punti base: è il numero con cui si confrontano. */
  bp: number;
  /** Percentuale, come la vuole il modulo. */
  slippage: number;
  makerFee: number;
  takerFee: number;
  feeMode: 'MAKER' | 'TAKER';
  nota: string;
}

export const VENUE: Venue[] = [
  {
    id: 'velora',
    nome: 'Velora',
    bp: 2.13,
    slippage: 0.025,
    makerFee: 0,
    takerFee: 0,
    feeMode: 'TAKER',
    nota: "In uso dal 24/08/2026 su BSC. 2,13 bp di esecuzione più 0,15 di gas, misurati sulla transazione reale del bot. La commissione della pool è già nel prezzo, quindi commissioni a zero.",
  },
  {
    id: 'hyperliquid',
    nome: 'Hyperliquid',
    bp: 5.03,
    slippage: 0.031,
    makerFee: 0,
    takerFee: 0.02,
    feeMode: 'TAKER',
    nota: 'Spot: taker 7 bp più il passaggio USDT0/USDC, con esecuzione 3,1 bp sotto il mid. Il vantaggio sul mid compensa in parte la commissione.',
  },
  {
    id: 'binance',
    nome: 'Binance',
    bp: 10,
    slippage: 0,
    makerFee: 0.1,
    takerFee: 0.1,
    feeMode: 'TAKER',
    nota: "Tariffa standard 0,1% per lato. Qui il costo è una commissione vera e va nel campo commissioni, non nello slippage.",
  },
  {
    id: '0x',
    nome: '0x',
    bp: 15.35,
    slippage: 0.154,
    makerFee: 0,
    takerFee: 0,
    feeMode: 'TAKER',
    nota: 'In uso fino al 24/08/2026. Di questi 15,35 bp, ben 15 sono commissione propria di 0x sopra il prezzo di mercato.',
  },
  {
    id: '1inch',
    nome: '1inch',
    bp: 41.07,
    slippage: 0.411,
    makerFee: 0,
    takerFee: 0,
    feeMode: 'TAKER',
    nota: 'Il peggiore fra quelli misurati su BSC.',
  },
  {
    id: 'zero',
    nome: 'Senza costi',
    bp: 0,
    slippage: 0,
    makerFee: 0,
    takerFee: 0,
    feeMode: 'MAKER',
    nota: "Nessun costo. Serve solo a isolare l'effetto della strategia: il risultato non è ottenibile.",
  },
];

/**
 * Erosione composta su `giri` giri completi: ogni giro paga due esecuzioni.
 *
 * Si mostra PRIMA di lanciare, non dopo: è l'unico modo di accorgersi che una
 * strategia da centoventi giri all'anno non regge i costi del venue scelto.
 */
export function erosione(bp: number, giri: number | null): number | null {
  if (!giri || !Number.isFinite(giri) || giri <= 0) return null;
  return (1 - Math.pow(1 - bp / 10000, giri * 2)) * 100;
}
